import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Ticket, Eye, EyeOff, AlertCircle, Shield, ArrowLeft, Loader2, MapPin, Users, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const TRAVEL_BG_IMAGE = "https://images.unsplash.com/photo-1552832230-c0197dd311b5?auto=format&fit=crop&w=1920&q=80";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, requires2FA, verify2FA, cancel2FA } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const redirectMessage = searchParams.get('message');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, rememberMe);
      
      if (result.requires2FA) {
        setLoading(false);
        return;
      }
      
      const user = result.user;
      toast.success('Welcome back!');
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'guide') {
        navigate('/guide');
      } else if (user.role === 'user') {
        navigate(user.user_type === 'member' ? '/member' : '/external');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await verify2FA(totpCode);
      
      const user = result.user;
      toast.success('Welcome back!');
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'guide') {
        navigate('/guide');
      } else if (user.role === 'user') {
        navigate(user.user_type === 'member' ? '/member' : '/external');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid 2FA code';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setTotpCode('');
    setError('');
  };

  // 2FA Verification Form
  if (requires2FA) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Background Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${TRAVEL_BG_IMAGE})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/80 to-indigo-600/60" />
          <div className="relative z-10 flex flex-col justify-center p-12 text-white">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Ticket className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold">Muoov</span>
            </div>
            <h2 className="text-4xl font-bold mb-4">Secure Access</h2>
            <p className="text-xl text-white/80 mb-8">
              Your account is protected with two-factor authentication
            </p>
            <div className="flex items-center gap-2 text-white/70">
              <Shield className="w-5 h-5" />
              <span>Enhanced security enabled</span>
            </div>
          </div>
        </div>

        {/* Right side - 2FA Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md animate-slide-up">
            <Card className="shadow-2xl border-0 bg-white">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-600/30">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">Two-Factor Authentication</CardTitle>
                <CardDescription>
                  Enter the 6-digit code from your authenticator app
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handle2FASubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="totpCode">Authentication Code</Label>
                    <Input
                      id="totpCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                      className="text-center text-2xl tracking-widest font-mono h-14 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                      autoFocus
                      data-testid="totp-code-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-lg shadow-indigo-600/30"
                    disabled={loading || totpCode.length !== 6}
                    data-testid="verify-2fa-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify'
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={handleCancel2FA}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Main Login Form
  return (
    <div className="min-h-screen flex">
      {/* Left side - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center transform scale-105"
          style={{ backgroundImage: `url(${TRAVEL_BG_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 via-indigo-800/70 to-purple-900/80" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
              <Ticket className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight">Muoov</span>
          </div>
          
          {/* Main content */}
          <div className="space-y-6">
            <h1 className="text-5xl font-bold leading-tight">
              Your Gateway to<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
                Unforgettable
              </span><br />
              Experiences
            </h1>
            <p className="text-xl text-white/80 max-w-md">
              Manage monument tickets, coordinate guides, and create seamless travel experiences.
            </p>
            
            {/* Features */}
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                <Ticket className="w-4 h-4 text-amber-300" />
                <span className="text-sm">Ticket Management</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                <MapPin className="w-4 h-4 text-amber-300" />
                <span className="text-sm">Guide Coordination</span>
              </div>
            </div>
          </div>
          
          {/* Bottom */}
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span>Collaborative ticketing for tour operators</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md animate-slide-up">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl mb-4 shadow-lg shadow-indigo-600/30">
              <Ticket className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Muoov</h1>
            <p className="text-slate-500 mt-1">Ticket Management System</p>
          </div>

          <Card className="shadow-2xl border-0 bg-white">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center text-slate-900">Welcome back</CardTitle>
              <CardDescription className="text-center text-slate-500">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              {redirectMessage && (
                <Alert className="mb-4 bg-blue-50 border-blue-200">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-700">
                    {redirectMessage}
                  </AlertDescription>
                </Alert>
              )}
              
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                    data-testid="email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="h-12 pr-10 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="rememberMe" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className="border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                    data-testid="remember-me-checkbox"
                  />
                  <Label htmlFor="rememberMe" className="text-sm text-slate-600 cursor-pointer">
                    Keep me logged in for 15 days
                  </Label>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-lg shadow-indigo-600/30 text-base font-medium"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </div>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Join as Guide CTA */}
          <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200/50 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Are you a Tour Guide?</h3>
                <p className="text-sm text-slate-600">Join our network of operators</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Benefit from streamlined reservations for your guiding services all year round. 
              Connect with tour operators who need your expertise!
            </p>
            <Link to="/guide/join">
              <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/30" data-testid="join-as-guide-btn">
                Join as a Guide
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-6">
            Already have a member account?{' '}
            <span className="text-indigo-600 font-medium">Contact your administrator</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
